/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { ITileListItemViewProps } from '@msdyn365-commerce-modules/tile-list';

import { ITileListItemProps } from '../definition-extensions/tile-list-item.ext.props.autogenerated';

/**
 *
 * ITileListItemViewProps component.
 * @param props - Component props.
 * @returns TileListView.
 */
const renderTileListItemContent = (props: ITileListItemViewProps & ITileListItemProps<{}>): JSX.Element => {
    const {
        heading,
        paragraph,
        backgroundImage,
        links,
        thumbnailImage,
        tileListContainer,
        tileListImageContainer,
        tileListThumbnailImageContainer,
        tileListHeadingContainer
    } = props;

    function _renderDropdownLinks(): JSX.Element {
        if (props.config.enableDropdown) {
            return (
                <div className='dropdown-content'>
                    <div className='dropdown-title'>
                        <b>{props.config.dropdownTitle}</b>
                    </div>
                    <div className='dropdown-links'>{links}</div>
                </div>
            );
        }

        return <></>;
    }

    function renderHoverImage(item?: Msdyn365.IImageData): React.ReactNode | null {
        // If image is null, we want to render the placeholder SVG - by passing image data with empty src

        const defaultImageSettings: Msdyn365.IImageSettings = {
            viewports: {
                xs: { q: 'w=328&h=412&m=6', w: 328, h: 412 },
                sm: { q: 'w=328&h=412&m=6', w: 328, h: 412 },
                md: { q: 'w=315&h=412&m=6', w: 315, h: 412 },
                lg: { q: 'w=315&h=412&m=6', w: 315, h: 412 },
                xl: { q: 'w=315&h=412&m=6', w: 315, h: 412 }
            },
            lazyload: true
        };
        const verifiedImageSettings = item?.imageSettings?.viewports ? item.imageSettings : defaultImageSettings;

        return (
            <Msdyn365.Image
                {...item}
                requestContext={props.context.request}
                gridSettings={props.context.request.gridSettings!}
                imageSettings={verifiedImageSettings}
                loadFailureBehavior='default'
                editProps={{
                    key: props.config.hoverImage ?? {},
                    requestContext: props.context.request,
                    moduleType: props.typeName,
                    imagePropertyName: 'image'
                }}
            />
        );
    }

    return (
        <>
            <Node {...tileListContainer}>
                <Node {...tileListImageContainer}>{backgroundImage}</Node>
                <Node {...tileListImageContainer}>{renderHoverImage(props.config.hoverImage)}</Node>
                {_renderDropdownLinks()}
                <Node {...tileListHeadingContainer}>{paragraph}</Node>
                <Node {...tileListThumbnailImageContainer}>{thumbnailImage}</Node>
            </Node>
            {heading}
        </>
    );
};

/**
 *
 * TileListItemViewProps component.
 * @param props - Component props.
 * @returns TileListView.
 */
export const TileListItemViewComponent: React.FC<ITileListItemViewProps & ITileListItemProps<{}>> = props => {
    const { tileListItem } = props;
    return <Module {...tileListItem}>{renderTileListItemContent(props)}</Module>;
};

export default TileListItemViewComponent;
